import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, type ApiError, type SuccessPlanResult } from '@whinerp/client';
import { formatDate, todayIn } from '@whinerp/contracts/format';
import { Button, Panel, SectionLabel } from '@whinerp/ui';
import { useErrorMessage, useFormat, useT } from '../../app/i18n.js';
import { useEntitlement } from '../../app/session.js';

/** A customer's success plan and business reviews (G-200). */

const INPUT = 'h-input rounded-control border border-control bg-surface px-2 text-body text-ink';
const STATUSES = ['on-track', 'at-risk', 'achieved', 'dropped'] as const;

export function SuccessPlanPanel({ accountId }: { accountId: string }): JSX.Element | null {
  const t = useT();
  const errorText = useErrorMessage();
  const format = useFormat();
  const queryClient = useQueryClient();
  const enabled = useEntitlement('F-080').enabled;
  const key = ['success-plan', accountId];
  const plan = useQuery<SuccessPlanResult, ApiError>({ queryKey: key, queryFn: () => api.successPlan({ path: { accountId } }), enabled });
  const put = (result: SuccessPlanResult) => queryClient.setQueryData(key, result);
  const [objective, setObjective] = useState({ title: '', measure: '', dueOn: '' });
  const [review, setReview] = useState({ summary: '', agreed: '' });
  const addObjective = useMutation({
    mutationFn: () => api.saveSuccessObjective({ path: { accountId }, body: { title: objective.title, measure: objective.measure, dueOn: objective.dueOn || null } }),
    onSuccess: (result) => {
      put(result);
      setObjective({ title: '', measure: '', dueOn: '' });
    },
  });
  const setStatus = useMutation({
    mutationFn: (input: { objectiveId: string; title: string; measure: string; dueOn: string | null; status: (typeof STATUSES)[number] }) =>
      api.saveSuccessObjective({ path: { accountId }, body: input }),
    onSuccess: put,
  });
  const addReview = useMutation({
    mutationFn: () => api.recordBusinessReview({ path: { accountId }, body: { heldOn: todayIn(format), summary: review.summary, agreed: review.agreed || null } }),
    onSuccess: (result) => {
      put(result);
      setReview({ summary: '', agreed: '' });
    },
  });
  if (!enabled || !plan.data) return null;
  const view = plan.data;
  return (
    <Panel data-region="success-plan">
      <div className="flex flex-col gap-3">
        <SectionLabel>{t('success.title')}</SectionLabel>
        <p className={`text-dense ${view.reviewOverdue ? 'text-warning-text' : 'text-ink-meta'}`} data-review-due={view.reviewOverdue ? 'overdue' : 'later'}>
          {view.reviewOverdue ? t('success.reviewOverdue') : t('success.reviewDue', { values: { date: formatDate(view.nextReviewDue, format) } })}
        </p>
        <ul className="flex flex-col gap-1 text-dense">
          {view.objectives.map((entry) => (
            <li key={entry.objectiveId} data-objective={entry.objectiveId} className="flex flex-wrap items-center gap-2">
              <span className="text-ink">{entry.title}</span>
              <span className="text-ink-600">{entry.measure}</span>
              {entry.dueOn ? <span className={entry.overdue ? 'text-danger-text' : 'text-ink-meta'}>{formatDate(entry.dueOn, format)}</span> : null}
              <select className={`${INPUT} ms-auto`} data-objective-status aria-label={t('success.status')} value={entry.status}
                onChange={(event) => setStatus.mutate({ objectiveId: entry.objectiveId, title: entry.title, measure: entry.measure, dueOn: entry.dueOn, status: event.target.value as 'on-track' })}>
                {STATUSES.map((status) => <option key={status} value={status}>{t(`success.status.${status}`)}</option>)}
              </select>
            </li>
          ))}
        </ul>
        <form className="flex flex-wrap items-end gap-2" data-objective-form onSubmit={(event) => { event.preventDefault(); addObjective.mutate(); }}>
          <input className={INPUT} required maxLength={200} data-objective-title placeholder={t('success.objective')} value={objective.title} onChange={(event) => setObjective({ ...objective, title: event.target.value })} />
          <input className={`${INPUT} flex-1`} required maxLength={300} data-objective-measure placeholder={t('success.measure')} value={objective.measure} onChange={(event) => setObjective({ ...objective, measure: event.target.value })} />
          <input className={INPUT} type="date" data-objective-due aria-label={t('success.due')} value={objective.dueOn} onChange={(event) => setObjective({ ...objective, dueOn: event.target.value })} />
          <Button size="small" type="submit" data-objective-add disabled={addObjective.isPending}>{t('success.addObjective')}</Button>
        </form>
        {view.reviews.length > 0 ? (
          <ul className="flex flex-col gap-1 text-dense" data-reviews>
            {view.reviews.slice(0, 5).map((entry) => (
              <li key={entry.reviewId} data-review={entry.reviewId} className="flex flex-col">
                <span className="text-ink-meta">{formatDate(entry.heldOn, format)}{entry.heldByName ? ` · ${entry.heldByName}` : ''}</span>
                <span className="text-ink">{entry.summary}</span>
                {entry.agreed ? <span className="text-ink-600">{t('success.agreed', { values: { text: entry.agreed } })}</span> : null}
              </li>
            ))}
          </ul>
        ) : null}
        <form className="flex flex-col gap-2" data-review-form onSubmit={(event) => { event.preventDefault(); addReview.mutate(); }}>
          <textarea className="rounded-control border border-control bg-surface p-2 text-body text-ink" required data-review-summary placeholder={t('success.reviewSummary')}
            value={review.summary} onChange={(event) => setReview({ ...review, summary: event.target.value })} />
          <input className={INPUT} data-review-agreed placeholder={t('success.reviewAgreed')} value={review.agreed} onChange={(event) => setReview({ ...review, agreed: event.target.value })} />
          <div><Button size="small" type="submit" data-review-save disabled={addReview.isPending}>{t('success.recordReview')}</Button></div>
        </form>
        {[addObjective.error, setStatus.error, addReview.error].map((error, index) => (error ? <p key={index} className="text-dense text-danger-text">{errorText(error as ApiError)}</p> : null))}
      </div>
    </Panel>
  );
}
